/**
 * Pending-Adyen-payment handoff — the payload that travels from the booking
 * iframe out to the top-level pay popup so Adyen/3DS can run in a context that
 * is not storage-partitioned or framed.
 *
 * The payload travels on the pay-tab URL hash, which survives storage partitioning
 * between the iframe and the popup. It carries only references: the Adyen session
 * blob itself stays on the payment record and the popup fetches it by checkoutRef.
 * Keeping the URL short matters — third-party tags on the pay page choke on very
 * long URLs.
 *
 * The popup reads the hash once and strips it, so the checkoutRef leaves the URL.
 * Nothing is persisted on purpose: a reload finds no payment and shows the
 * not-found screen rather than mounting (and, for auto-submit methods, submitting)
 * the same session again.
 */

import { markPaymentPopup } from '@/helpers/embed';
import type { WidgetResourceType } from '@/helpers/embedMessages';
import { postToOpener } from '@/helpers/embedPopup';

export const PAY_POPUP_NAME_PREFIX = 'bd-payment-';

export type PendingAdyenPayment = {
  // The Adyen session the top-level pay tab mounts (e.g. card/3DS); its data is
  // fetched from the backend by checkoutRef. A payment with nothing to mount never
  // escapes the frame, so there is no session-less payload.
  sessionId: string;
  checkoutRef: string;
  // What is being paid for. Carried so the pay tab can build the reconcile URL for
  // the right resource instead of assuming a booking; mirrors the `resourceType` on
  // `BD_WIDGET_PAYMENT_COMPLETE`, which reports the outcome back to the frame.
  resourceId: string;
  resourceType: WidgetResourceType;
  paymentMethodType: number;
  // The amount the pay tab shows in wallet sheets (Google Pay); the session itself
  // carries the authoritative charge amount.
  amount?: number;
  // Whether the checkout ran as a logged-in visitor, which is what decides if a
  // new card is stored. The pay tab labels the card form from this: its own
  // session is a different identity from the iframe's (storage partitioning),
  // and the iframe's may be widget-own with no counterpart in the popup.
  hasUser?: boolean;
};

function encodePayload(data: PendingAdyenPayment): string {
  // URL-safe base64 of the JSON payload, carried on the pay-tab URL hash.
  return btoa(encodeURIComponent(JSON.stringify(data)));
}

/** The pay URL's hash, as the frame hands it to the popup it opened. */
export function buildPayPopupHash(payment: PendingAdyenPayment): string {
  return `#${encodePayload(payment)}`;
}

// Inside the frame these read the browser window, so the popup is centred on it.
export function getPayPopupFeatures(): string {
  const w = 700;
  const h = 760;
  const left = window.screenX + Math.max(0, (window.outerWidth - w) / 2);
  const top = window.screenY + Math.max(0, (window.outerHeight - h) / 2);
  return `popup=yes,width=${w},height=${h},left=${left},top=${top}`;
}

/**
 * Popup → frame, over the pinned same-origin channel in `embedPopup`. The first
 * report from a popup is final; the frame drops anything after it.
 */
export type PaymentResultMessage =
  | { type: 'BD_WIDGET_PAYMENT_COMPLETE'; resourceId: string; resourceType: WidgetResourceType }
  | { type: 'BD_WIDGET_PAYMENT_ERROR'; errorCode?: string; errorMessage?: string };

const isOptionalString = (value: unknown) => value === undefined || typeof value === 'string';

export function isPaymentResultMessage(data: unknown): data is PaymentResultMessage {
  if (typeof data !== 'object' || data === null) return false;
  const d = data as Record<string, unknown>;
  switch (d.type) {
    case 'BD_WIDGET_PAYMENT_COMPLETE':
      return typeof d.resourceId === 'string' && (d.resourceType === 'booking' || d.resourceType === 'bundle');
    case 'BD_WIDGET_PAYMENT_ERROR':
      return isOptionalString(d.errorCode) && isOptionalString(d.errorMessage);
    default:
      return false;
  }
}

// `postToOpener` infers its type from the argument, so only a fixed parameter type checks the message shape.
export const postPaymentResult = (message: PaymentResultMessage) => postToOpener(message);

// The hash is untrusted input; a junk payload must not be handed to the page.
// The fields checked here are the ones interpolated into URLs downstream (the
// session fetch and the reconcile URL).
function isPendingAdyenPayment(value: unknown): value is PendingAdyenPayment {
  if (typeof value !== 'object' || value === null) return false;
  const { sessionId, checkoutRef, resourceId, resourceType } = value as Record<string, unknown>;
  return (
    typeof sessionId === 'string' &&
    sessionId.length > 0 &&
    typeof checkoutRef === 'string' &&
    checkoutRef.length > 0 &&
    typeof resourceId === 'string' &&
    resourceId.length > 0 &&
    (resourceType === 'booking' || resourceType === 'bundle')
  );
}

function decodePayload(raw: string): PendingAdyenPayment | null {
  try {
    const parsed: unknown = JSON.parse(decodeURIComponent(atob(raw)));
    return isPendingAdyenPayment(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function stripHash(): void {
  try {
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
  } catch {
    /* ignore */
  }
}

/**
 * Read the pending payment in the top-level pay tab. The hash payload is returned
 * once and stripped from the URL; a later call (a reload) finds nothing pending.
 */
export function consumePendingAdyenPayment(): PendingAdyenPayment | null {
  const hash = window.location.hash.replace(/^#/, '');
  const fromHash = hash ? decodePayload(hash) : null;
  if (!fromHash) return null;

  // A valid payload our escape flow encoded is a widget-only signal that this
  // window is the pay popup — mark it now, before any Adyen redirect can strip
  // the transient window.name signal.
  markPaymentPopup();
  stripHash();
  return fromHash;
}
